import React, { useRef, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Textarea,
  Tooltip,
  VStack,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import {
  AddIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  RepeatIcon,
} from '@chakra-ui/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import Card from 'components/card/Card';

import {
  apiAdminGetBadges,
  apiAdminCreateBadge,
  apiAdminUpdateBadge,
  apiAdminReorderBadges,
} from 'api';

interface Badge {
  id: number;
  name: string;
  description: string;
  image_url: string | null;
  is_active: boolean;
  order: number;
}

const TABLE_HEADERS = [
  'Order',
  'Preview',
  'Name',
  'Description',
  'Active',
  'Actions',
];

const emptyForm = () => ({
  name: '',
  description: '',
  order: 0,
  is_active: true,
  imageFile: undefined as File | undefined,
});

const BadgesView: React.FC = () => {
  const toast = useToast();
  const qc = useQueryClient();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const fileRef = useRef<HTMLInputElement>(null);

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const hoverBg = useColorModeValue('gray.200', 'whiteAlpha.100');
  const inputBg = useColorModeValue('white', 'navy.900');

  const [editing, setEditing] = useState<Badge | null>(null);
  const [form, setForm] = useState(emptyForm());
  const [preview, setPreview] = useState<string | null>(null);

  const {
    data: badges = [],
    isLoading,
    isFetching,
    refetch,
  } = useQuery<Badge[]>({
    queryKey: ['admin-badges'],
    queryFn: () =>
      apiAdminGetBadges().then((r: any) =>
        Array.isArray(r) ? r : (r?.data ?? []),
      ),
  });

  const inv = () => qc.invalidateQueries({ queryKey: ['admin-badges'] });

  const createMutation = useMutation({
    mutationFn: (fd: FormData) => apiAdminCreateBadge(fd),
    onSuccess: () => {
      toast({ title: 'Badge created', status: 'success' });
      inv();
      onClose();
    },
    onError: () => toast({ title: 'Failed to create badge', status: 'error' }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, fd }: { id: number; fd: FormData }) =>
      apiAdminUpdateBadge(id, fd),
    onSuccess: () => {
      toast({ title: 'Badge updated', status: 'success' });
      inv();
      onClose();
    },
    onError: () => toast({ title: 'Failed to update badge', status: 'error' }),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, is_active }: { id: number; is_active: boolean }) => {
      const fd = new FormData();
      fd.append('is_active', String(is_active));
      return apiAdminUpdateBadge(id, fd);
    },
    onSuccess: inv,
    onError: (err: any) => {
      const msg =
        err?.is_active?.[0] || err?.detail || 'Failed to update badge';
      toast({ title: msg, status: 'error' });
    },
  });

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.svg')) {
      toast({ title: 'Please upload an SVG file', status: 'warning' });
      return;
    }
    setForm((f) => ({ ...f, imageFile: file }));
    setPreview(URL.createObjectURL(file));
  };

  const handleSubmit = () => {
    if (!form.name.trim()) {
      toast({ title: 'Name is required', status: 'warning' });
      return;
    }
    const fd = new FormData();
    if (form.imageFile) fd.append('image', form.imageFile);
    fd.append('name', form.name);
    fd.append('description', form.description);
    fd.append('order', String(form.order));
    fd.append('is_active', String(form.is_active));
    editing
      ? updateMutation.mutate({ id: editing.id, fd })
      : createMutation.mutate(fd);
  };

  const reorderMutation = useMutation({
    mutationFn: (items: { id: number; order: number }[]) =>
      apiAdminReorderBadges(items),
    onSuccess: inv,
    onError: () => toast({ title: 'Failed to reorder', status: 'error' }),
  });

  const handleReorder = (id: number, direction: 'up' | 'down') => {
    const s = [...badges].sort((a, b) => a.order - b.order);
    const idx = s.findIndex((b) => b.id === id);
    if (direction === 'up' && idx === 0) return;
    if (direction === 'down' && idx === s.length - 1) return;
    const swap = direction === 'up' ? idx - 1 : idx + 1;
    // Swap order values as a single atomic batch
    reorderMutation.mutate([
      { id: s[idx].id, order: s[swap].order },
      { id: s[swap].id, order: s[idx].order },
    ]);
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setPreview(null);
    onOpen();
  };
  const openEdit = (b: Badge) => {
    setEditing(b);
    setForm({
      name: b.name,
      description: b.description,
      order: b.order,
      is_active: b.is_active,
      imageFile: undefined,
    });
    setPreview(b.image_url);
    onOpen();
  };
  const isBusy = createMutation.isPending || updateMutation.isPending;
  const sorted = [...badges].sort((a, b) => a.order - b.order);

  return (
    <Card
      flexDirection="column"
      w="100%"
      px="0px"
      overflowX={{ sm: 'scroll', lg: 'hidden' }}
    >
      <Flex px="20px" justifyContent="space-between" align="center" mb="20px">
        <Text
          color={textColor}
          fontSize="22px"
          fontWeight="700"
          lineHeight="100%"
        >
          Badges
        </Text>
        <Flex gap={2}>
          <Tooltip label="Refresh badges" hasArrow>
            <IconButton
              aria-label="Refresh badges"
              icon={<RepeatIcon />}
              onClick={() => refetch()}
              variant="ghost"
              isLoading={isLoading || isFetching}
            />
          </Tooltip>
          <Button
            leftIcon={<AddIcon />}
            variant="brand"
            size="md"
            onClick={openCreate}
          >
            New Badge
          </Button>
        </Flex>
      </Flex>

      <Table variant="simple" color="gray.500">
        <Thead>
          <Tr>
            {TABLE_HEADERS.map((header) => (
              <Th
                key={header}
                pe="10px"
                borderColor={borderColor}
                color="gray.400"
              >
                {header}
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {isLoading && (
            <Tr>
              <Td colSpan={TABLE_HEADERS.length} textAlign="center">
                <Flex align="center" justify="center" mt="20px">
                  <Spinner size="sm" mr="10px" /> Loading badges...
                </Flex>
              </Td>
            </Tr>
          )}

          {sorted.map((b, idx) => (
            <Tr
              key={b.id}
              cursor="pointer"
              opacity={b.is_active ? 1 : 0.5}
              _hover={{ bg: hoverBg }}
              onClick={() => openEdit(b)}
            >
              <Td onClick={(e) => e.stopPropagation()} cursor="default">
                <HStack spacing={1}>
                  <IconButton
                    aria-label="Move up"
                    size="xs"
                    variant="ghost"
                    icon={<ChevronUpIcon boxSize={4} />}
                    isDisabled={idx === 0}
                    onClick={() => handleReorder(b.id, 'up')}
                  />
                  <Text color={textColor} fontSize="sm" fontWeight="700">
                    {b.order}
                  </Text>
                  <IconButton
                    aria-label="Move down"
                    size="xs"
                    variant="ghost"
                    icon={<ChevronDownIcon boxSize={4} />}
                    isDisabled={idx === sorted.length - 1}
                    onClick={() => handleReorder(b.id, 'down')}
                  />
                </HStack>
              </Td>
              <Td>
                {b.image_url ? (
                  <Box w="40px" h="40px">
                    <img
                      src={b.image_url}
                      alt={b.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                      }}
                    />
                  </Box>
                ) : (
                  <Box w="40px" h="40px" bg={hoverBg} borderRadius="md" />
                )}
              </Td>
              <Td>
                <Text color={textColor} fontSize="sm" fontWeight="700">
                  {b.name}
                </Text>
              </Td>
              <Td>
                <Text
                  color={mutedColor}
                  fontSize="sm"
                  noOfLines={2}
                  maxW="320px"
                >
                  {b.description || '—'}
                </Text>
              </Td>
              <Td onClick={(e) => e.stopPropagation()} cursor="default">
                <Switch
                  size="sm"
                  isChecked={b.is_active}
                  onChange={(e) =>
                    toggleMutation.mutate({
                      id: b.id,
                      is_active: e.target.checked,
                    })
                  }
                />
              </Td>
              <Td onClick={(e) => e.stopPropagation()} cursor="default">
                <Button size="sm" variant="outline" onClick={() => openEdit(b)}>
                  Edit
                </Button>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      {!isLoading && sorted.length === 0 && (
        <Box textAlign="center" w="100%" mt="20px" color={mutedColor}>
          No badges yet. Click "New Badge" to create one.
        </Box>
      )}

      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editing ? 'Edit Badge' : 'Add Badge'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel fontSize="sm">
                  Badge SVG{' '}
                  <Text as="span" fontSize="xs" color={mutedColor}>
                    (optional)
                  </Text>
                </FormLabel>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".svg"
                  style={{ display: 'none' }}
                  onChange={handleFile}
                />
                <Box
                  border="2px dashed"
                  borderColor={preview ? 'orange.400' : borderColor}
                  borderRadius="md"
                  p={4}
                  textAlign="center"
                  cursor="pointer"
                  onClick={() => fileRef.current?.click()}
                  _hover={{ borderColor: 'orange.400' }}
                  minH="100px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  {preview ? (
                    <img
                      src={preview}
                      alt="preview"
                      style={{
                        maxHeight: '80px',
                        maxWidth: '100%',
                        objectFit: 'contain',
                      }}
                    />
                  ) : (
                    <VStack spacing={1}>
                      <Text fontSize="sm" color={mutedColor}>
                        Click to upload SVG
                      </Text>
                      <Text fontSize="xs" color={mutedColor}>
                        SVG only — leave blank for text-only badge
                      </Text>
                    </VStack>
                  )}
                </Box>
                {preview && (
                  <Button
                    size="xs"
                    mt={1}
                    variant="ghost"
                    colorScheme="red"
                    onClick={() => {
                      setPreview(null);
                      setForm((f) => ({ ...f, imageFile: undefined }));
                    }}
                  >
                    Remove
                  </Button>
                )}
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm">Name</FormLabel>
                <Input
                  variant="main"
                  size="md"
                  bg={inputBg}
                  value={form.name}
                  placeholder="e.g. President's Club"
                  onChange={(e) =>
                    setForm((f) => ({ ...f, name: e.target.value }))
                  }
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">Description</FormLabel>
                <Textarea
                  variant="main"
                  size="md"
                  bg={inputBg}
                  value={form.description}
                  placeholder="Optional description"
                  onChange={(e) =>
                    setForm((f) => ({ ...f, description: e.target.value }))
                  }
                  rows={4}
                  minH="120px"
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">Order</FormLabel>
                <Input
                  variant="main"
                  size="md"
                  bg={inputBg}
                  type="number"
                  value={form.order}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, order: Number(e.target.value) }))
                  }
                />
              </FormControl>

              <FormControl display="flex" alignItems="center" gap={3}>
                <FormLabel fontSize="sm" mb={0}>
                  Active
                </FormLabel>
                <Switch
                  isChecked={form.is_active}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, is_active: e.target.checked }))
                  }
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button variant="brand" isLoading={isBusy} onClick={handleSubmit}>
              {editing ? 'Save changes' : 'Create badge'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Card>
  );
};

export default BadgesView;
